import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "bulkAction", "setAllCheckbox", "totalCheckboxes", "selectedCheckboxes", "columns", "filters", "counterContainer", "slash", "prospectsText", "tabsContainer"]

  connect() {
    if (!this.hasSetAllCheckboxTarget) return

    this.setupRowShift();
    this.bulkActionTargets.forEach(element => {
      element.addEventListener("click", this.performBulkAction.bind(this))
    });

    this.setAllCheckboxTarget.addEventListener("change", this.setAllCheckboxes.bind(this))

    this.totalCheckboxesTarget.innerText = this.element.querySelectorAll("input[type='checkbox'][name='id[]']").length
    this.element.querySelectorAll("input[type='checkbox']").forEach(checkbox => {
      checkbox.addEventListener("change", this.updateSelectedCheckboxes.bind(this))
    })
    this.allCheckboxes = this.element.querySelectorAll("input[type='checkbox'][name='id[]']")
    
    // Ensure bulk actions are hidden initially
    this.bulkActionTargets.forEach(element => {
      element.classList.add('opacity-0')
      element.classList.remove('opacity-100')
    })
    
    // Set initial counter display state
    this.toggleCounterDisplay(0, this.allCheckboxes.length)
    
    // Listen for Turbo Stream updates to reset checkbox states
    this.element.addEventListener('turbo:stream-render', this.handleTurboStreamUpdate.bind(this))
  }

  setupRowShift() {
    // Prevent native checkbox toggle when shift-clicking directly on checkboxes
    // This must be in capture phase to run before the checkbox's native behavior
    this.element.querySelectorAll("input[type='checkbox'][name='id[]']").forEach(checkbox => {
      checkbox.addEventListener("click", e => {
        if (e.shiftKey && this.lastClickedRow) {
          e.preventDefault()
        }
      }, true) // capture phase
    })

    this.element.querySelectorAll("tbody tr").forEach(row => {
      row.addEventListener("click", e => {
        var currentRow = e.target
        if (currentRow.tagName !== "TR") currentRow = e.target.closest("tr")

        if (e.shiftKey && this.lastClickedRow) {
          e.preventDefault()
          const allRows = Array.from(this.element.querySelectorAll("tbody tr"))
          const lastIndex = allRows.indexOf(this.lastClickedRow)
          const currentIndex = allRows.indexOf(currentRow)

          const [start, end] = lastIndex < currentIndex
            ? [lastIndex, currentIndex]
            : [currentIndex, lastIndex]

          allRows.slice(start, end + 1).forEach(r => {
            const checkbox = r.querySelector("input[type='checkbox'][name='id[]']")
            checkbox.checked = true
          })
          this.updateSelectedCheckboxes()
        }

        this.lastClickedRow = currentRow
      })
    })
  }

  openColumns() {
    this.openDialog(this.columnsTarget.querySelector("dialog"))
  }

  openFilters() {
    this.openDialog(this.filtersTarget.querySelector("dialog"))
  }

  openDialog(dialog) {
    dialog?.showModal()
    dialog?.focus()
  }

  updateSelectedCheckboxes(event) {
    var selectedCheckboxes = Array.from(this.allCheckboxes).filter(checkbox => checkbox.checked).length
    this.selectedCheckboxesTarget.innerText = selectedCheckboxes

    // Update the indeterminate state and checked state of the "set all" checkbox
    const totalCheckboxes = this.allCheckboxes.length
    if (selectedCheckboxes === 0) {
      this.setAllCheckboxTarget.indeterminate = false
      this.setAllCheckboxTarget.checked = false
    } else if (selectedCheckboxes === totalCheckboxes) {
      this.setAllCheckboxTarget.indeterminate = false
      this.setAllCheckboxTarget.checked = true
    } else {
      this.setAllCheckboxTarget.indeterminate = true
      this.setAllCheckboxTarget.checked = false
    }
    
    // Toggle bulk actions visibility and counter display
    this.toggleBulkActions(selectedCheckboxes)
    this.toggleCounterDisplay(selectedCheckboxes, totalCheckboxes)
  }

  performBulkAction(event) {
    const button = event.currentTarget

    if (!button.dataset.turboConfirm || confirm(button.dataset.turboConfirm)) {
      const path = button.dataset.path
      const httpMethod = button.dataset.httpMethod

      this.formTarget.querySelector(`input[name="_method"]`).value = httpMethod
      this.formTarget.action = path

      this.formTarget.requestSubmit()
      
      // Reset checkbox states immediately after submitting the form
      setTimeout(() => {
        this.resetCheckboxStates()
      }, 100)
    }
  }

  setAllCheckboxes(event) {
    this.formTarget.querySelectorAll("input[type='checkbox']").forEach(checkbox => {
      checkbox.checked = event.currentTarget.checked
    })

    // Reset indeterminate state when "set all" is clicked
    this.setAllCheckboxTarget.indeterminate = false
    
    // Update bulk actions visibility and counter display
    const selectedCount = event.currentTarget.checked ? this.allCheckboxes.length : 0
    this.toggleBulkActions(selectedCount)
    this.toggleCounterDisplay(selectedCount, this.allCheckboxes.length)
  }

  toggleBulkActions(selectedCount) {
    const bulkActionsContainer = this.bulkActionTargets[0]?.closest('.flex.gap-2')

    this.bulkActionTargets.forEach(element => {
      element.classList.toggle('opacity-100', selectedCount > 0)
      element.classList.toggle('opacity-0', selectedCount === 0)
    })

    if (bulkActionsContainer) {
      bulkActionsContainer.classList.toggle('hidden', selectedCount === 0)
    }

    if (this.hasTabsContainerTarget) {
      const wrapper = this.tabsContainerTarget.parentElement
      this.tabsContainerTarget.classList.toggle('hidden', selectedCount > 0)
      if (wrapper) wrapper.classList.toggle('hidden', selectedCount > 0)
    }
  }

  toggleCounterDisplay(selectedCount, totalCount) {
    if (selectedCount === 0) {
      // Show "n Prospects" format
      this.selectedCheckboxesTarget.innerText = totalCount
      this.slashTarget.classList.add('hidden')
      this.totalCheckboxesTarget.classList.add('hidden')
      this.prospectsTextTarget.classList.remove('hidden')
    } else {
      // Show "selected/total" format
      this.selectedCheckboxesTarget.innerText = selectedCount
      this.slashTarget.classList.remove('hidden')
      this.totalCheckboxesTarget.classList.remove('hidden')
      this.prospectsTextTarget.classList.add('hidden')
    }
  }

  handleTurboStreamUpdate(event) {
    // Reset all checkbox states after Turbo Stream update
    this.resetCheckboxStates()
  }

  resetCheckboxStates() {
    // Uncheck all checkboxes
    this.allCheckboxes = this.element.querySelectorAll("input[type='checkbox'][name='id[]']")
    this.allCheckboxes.forEach(checkbox => {
      checkbox.checked = false
    })
    
    // Manually trigger the updateSelectedCheckboxes logic to properly sync master checkbox
    this.updateSelectedCheckboxes({})
  }
}
